import styles from './styles.module.less';
import { AiIcon } from '../../ai/aiIcon/aiIcon';
import { Tooltip, Popover, Input, Button } from 'antd';
import { useState } from 'react';
import {
    EditOutlined,
    QuestionCircleOutlined,
    LikeOutlined,
    LikeFilled,
    DislikeOutlined,
    DislikeFilled,
    ReloadOutlined,
    DeleteOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import {
    RECOMMENDED_REPLY_CLEAR_TRACKING_ID,
    RECOMMENDED_REPLY_EDIT_TRACKING_ID,
    RECOMMENDED_REPLY_REROLL_TRACKING_ID,
    RECOMMENDED_REPLY_THUMBS_DOWN_TRACKING_ID,
    RECOMMENDED_REPLY_THUMBS_UP_TRACKING_ID,
} from '@copilot/common/tracking/userpilotEventConsts';
import { isNil } from 'lodash';
import { FEEDBACK_OPTIONS, FeedbackOption } from '@copilot/data/responses/interface';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';

type RecommendedMessageViewProps = Readonly<{
    onClick: () => void;
    message: string;
    style?: React.CSSProperties;
    feedback?: FeedbackOption;
    onFeedbackChange?: (feedbackType: FeedbackOption, feedbackComment?: string) => void;
    onRegenerate?: () => Promise<boolean>;
    onDelete?: () => void;
    isRegenerating?: boolean;
}>;

/**
 * A component that displays a recommended message
 * @param onClick - The function to call when the recommended message is clicked
 * @param message - The message to display
 * @param style - The style to apply to the component
 * @param feedback - The current feedback type (controlled from parent)
 * @param onFeedbackChange - Callback when feedback changes (thumbs up auto-submits, thumbs down opens popover)
 * @param onRegenerate - Callback to regenerate the message recommendation
 * @param onDelete - Callback to clear/delete the recommendation
 */
export function RecommendedMessageView({
    onClick,
    message,
    style,
    feedback,
    onFeedbackChange,
    onRegenerate,
    onDelete,
    isRegenerating,
}: RecommendedMessageViewProps) {
    const [localFeedback, setFeedback] = useState<FeedbackOption | undefined>(feedback);
    const [feedbackPopoverVisible, setFeedbackPopoverVisible] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const { isReplyRecommendationOptionsEnabled } = useFeatureFlags();

    function onDeleteClick() {
        if (isRegenerating) return;
        onDelete?.();
    }

    function onThumbsUpClick() {
        if (isRegenerating) return;
        setFeedback(FEEDBACK_OPTIONS.up);
        onFeedbackChange?.(FEEDBACK_OPTIONS.up);
    }

    function onThumbsDownClick() {
        if (isRegenerating) return;
        setFeedback(FEEDBACK_OPTIONS.down);
        setFeedbackPopoverVisible(true);
    }

    async function onRegenerateClick() {
        if (isRegenerating) return;
        const isSuccess = await onRegenerate?.();
        if (isSuccess) {
            setFeedback(undefined);
        }
    }

    const handleMainClick = () => {
        if (isRegenerating) return;
        onClick();
    };

    function onFeedbackSubmit() {
        if (isNil(localFeedback)) return;
        onFeedbackChange?.(localFeedback, feedbackText);
        setFeedbackPopoverVisible(false);
        setFeedbackText('');
    }

    function onFeedbackCancel() {
        setFeedbackPopoverVisible(false);
        setFeedbackText('');
        setFeedback(undefined);
    }

    function onPopoverVisibilityChange(visible: boolean) {
        if (!visible) {
            setFeedbackPopoverVisible(false);
            setFeedbackText('');
            setFeedback(undefined);
        }
    }

    const renderFeedbackPopover = () => (
        <div className={styles.feedbackPopover}>
            <div className={styles.feedbackTitle}>Help us make replies smarter. Got a minute?</div>
            <div className={styles.feedbackDescription}>
                Help us understand what worked, what didn't, or how we can make this reply more
                helpful.
            </div>
            <Input.TextArea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Your feedback..."
                rows={4}
                className={styles.feedbackTextArea}
            />
            <div className={styles.feedbackActions}>
                <Button type="default" onClick={onFeedbackCancel}>
                    Cancel
                </Button>
                <Button type="primary" onClick={onFeedbackSubmit}>
                    Submit
                </Button>
            </div>
        </div>
    );

    return (
        <div
            className={`${styles.recommendedMessagePreview} ${
                isRegenerating ? styles.regenerating : ''
            }`}
            role="button"
            tabIndex={0}
            style={style}
            data-tracking-id={RECOMMENDED_REPLY_EDIT_TRACKING_ID}
        >
            <div className={styles.recommendedHeader}>
                <div className={styles.recommendedHeaderLeft}>
                    <span>Recommended Response</span>
                    <AiIcon />
                    <Tooltip title="Use our AI-suggested reply to respond faster and keep the conversation moving.">
                        <QuestionCircleOutlined className={styles.questionIcon} />
                    </Tooltip>
                </div>
                <div className={styles.recommendedActions}>
                    {isReplyRecommendationOptionsEnabled && (
                        <>
                            <Popover
                                content={renderFeedbackPopover()}
                                title={null}
                                trigger={['click']}
                                open={feedbackPopoverVisible}
                                onOpenChange={onPopoverVisibilityChange}
                                placement="topRight"
                                className={styles.feedbackPopoverOverlay}
                            >
                                <div className={styles.thumbsContainer}>
                                    <Tooltip title="Good Response">
                                        {localFeedback === FEEDBACK_OPTIONS.up ? (
                                            <LikeFilled
                                                className={`${styles.actionIcon} ${styles.activeThumb}`}
                                            />
                                        ) : (
                                            <LikeOutlined
                                                className={`${styles.actionIcon} ${
                                                    localFeedback === FEEDBACK_OPTIONS.down
                                                        ? styles.hidden
                                                        : ''
                                                }`}
                                                onClick={onThumbsUpClick}
                                                data-tracking-id={
                                                    RECOMMENDED_REPLY_THUMBS_UP_TRACKING_ID
                                                }
                                            />
                                        )}
                                    </Tooltip>
                                    <Tooltip title="Bad Response">
                                        {localFeedback === FEEDBACK_OPTIONS.down ? (
                                            <DislikeFilled
                                                className={`${styles.actionIcon} ${styles.activeThumb}`}
                                            />
                                        ) : (
                                            <DislikeOutlined
                                                className={`${styles.actionIcon} ${
                                                    localFeedback === FEEDBACK_OPTIONS.up
                                                        ? styles.hidden
                                                        : ''
                                                }`}
                                                onClick={onThumbsDownClick}
                                                data-tracking-id={
                                                    RECOMMENDED_REPLY_THUMBS_DOWN_TRACKING_ID
                                                }
                                            />
                                        )}
                                    </Tooltip>
                                </div>
                            </Popover>

                            <Tooltip title="Regenerate">
                                {isRegenerating ? (
                                    <LoadingOutlined className={styles.actionIcon} />
                                ) : (
                                    <ReloadOutlined
                                        className={styles.actionIcon}
                                        onClick={() => {
                                            onRegenerateClick();
                                        }}
                                        data-tracking-id={RECOMMENDED_REPLY_REROLL_TRACKING_ID}
                                    />
                                )}
                            </Tooltip>

                            <Tooltip title="Delete">
                                <DeleteOutlined
                                    className={styles.actionIcon}
                                    onClick={() => onDeleteClick()}
                                    data-tracking-id={RECOMMENDED_REPLY_CLEAR_TRACKING_ID}
                                />
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title="Edit">
                        <EditOutlined className={styles.actionIcon} onClick={onClick} />
                    </Tooltip>
                </div>
            </div>
            <div onClick={handleMainClick} className={styles.recommendedBody}>
                {message}
            </div>
        </div>
    );
}
